import { useState, useEffect } from "react";
import { format } from "date-fns";
import { useFirestore } from "../../firebase-files/hooks/useFirestore";
import { useAuthContext } from "../../firebase-files/hooks/useAuthContext";
import { useCollection } from "../../firebase-files/hooks/useCollection";
import Select from "react-select";

const VendorDetailsEmpDetails = ({ onComplete }) => {
  const { user } = useAuthContext();
  const { updateDocument } = useFirestore("organisations");
  const { documents: organisations } = useCollection("organisations");

  const vendorOrganisations = organisations?.find(
    (org) => org.organisationId === user?.organisationId
  );

  // UI states
  const [isEditing, setIsEditing] = useState(false);
  const [employeeDetailUpdateMessage, setEmployeeDetailUpdateMessage] =
    useState("");
  const [isEdUpdating, setIsEdUpdating] = useState(false);
  const [edMessageType, setEdMessageType] = useState("");

  // form state
  const [vendorDetailFormData, setVendorDetailFormData] = useState({
    organisationName: "",
    dateOfJoining: "",
    department: null, // department will store {value, label}
  });

  // department options
  const departmentOptions = [
    { value: "automobile", label: "Automobile" },
    { value: "healthcare", label: "Healthcare" },
    { value: "IT", label: "Informatiion Technology" },
    { value: "engineering", label: "Engineering" },
    { value: "fashion", label: "Fashion" },
  ];

  // Cancel → restore
  const handleCancelClick = () => {
    setIsEditing(false);
    if (vendorOrganisations) {
      setVendorDetailFormData({
        organisationName: vendorOrganisations.organisationName || "",
        dateOfJoining: vendorOrganisations.dateOfJoining || "",
        department: vendorOrganisations.department
          ? departmentOptions.find(
              (opt) => opt.value === vendorOrganisations.department
            )
          : null,
      });
    }
  };

  // Populate when doc loads
  useEffect(() => {
    if (vendorOrganisations) {
      setVendorDetailFormData({
        organisationName: vendorOrganisations.organisationName || "",
        dateOfJoining: vendorOrganisations.dateOfJoining || "",
        department: vendorOrganisations.department
          ? departmentOptions.find(
              (opt) => opt.value === vendorOrganisations.department
            )
          : null,
      });
    }
  }, [vendorOrganisations]);

  // Validation
  useEffect(() => {
    if (!vendorDetailFormData) return;

    const allFilled = Object.values(vendorDetailFormData).every(
      (v) => v !== ""
    );

    // Only call onComplete if value actually changes
    onComplete?.(allFilled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vendorDetailFormData.organisationName,
    vendorDetailFormData.dateOfJoining,
  ]);

  // Save
  const handleUpdateEmployeeDetails = async () => {
    const { organisationName, dateOfJoining, department } =
      vendorDetailFormData;

    if (!organisationName || !dateOfJoining || !department) {
      setEmployeeDetailUpdateMessage("Please fill all required fields.");
      setEdMessageType("error_msg");
      setTimeout(() => {
        setEmployeeDetailUpdateMessage("");
        setEdMessageType("");
      }, 4000);
      return;
    }

    setIsEdUpdating(true);
    setEmployeeDetailUpdateMessage("");

    try {
      await updateDocument(vendorOrganisations.id, {
        organisationName,
        dateOfJoining,
        department: department.value, // store value in DB
      });
      setEdMessageType("success_msg");
      setEmployeeDetailUpdateMessage("Details updated successfully!");
      setTimeout(() => setIsEditing(false), 4000);
    } catch (error) {
      console.error("Error updating Staff details:", error);
      setEdMessageType("error_msg");
      setEmployeeDetailUpdateMessage(
        "Failed to update Staff details. Please try again."
      );
    } finally {
      setIsEdUpdating(false);
      setTimeout(() => {
        setEmployeeDetailUpdateMessage("");
        setEdMessageType("");
      }, 4000);
    }
  };

  return (
    <div className="pd-cards animate-slide-in">
      <div className="pd-heading">
        <h2>Organisation Details</h2>
        {user &&
          vendorOrganisations &&
          vendorOrganisations.isApproved !== true &&
          vendorOrganisations.isActive &&
          !isEditing &&
          ["admin", "superAdmin"].includes(user.role) && (
            <div className="pd-edit-state" onClick={() => setIsEditing(true)}>
              <span className="material-symbols-outlined">edit</span>
              Edit
            </div>
          )}
      </div>

      <div className="pd-data">
        {!isEditing ? (
          <>
            <div className="pd-data-element">
              <div className="pd-icon">
                <img src="/assets/img/id.png" alt="" />
              </div>
              <div className="pd-data-value">
                <h6>Organisation Name</h6>
                <p>
                  {vendorOrganisations?.organisationName || "Not provided yet"}
                </p>
              </div>
            </div>
            <div className="pd-data-element">
              <div className="pd-icon">
                <img src="/assets/img/VisitingDays.png" alt="" />
              </div>
              <div className="pd-data-value">
                <h6>Date of joining</h6>
                <p>
                  {vendorOrganisations?.dateOfJoining
                    ? format(
                        new Date(vendorOrganisations.dateOfJoining),
                        "dd-MMM-yyyy"
                      )
                    : "Not provided yet"}
                </p>
              </div>
            </div>
            <div className="pd-data-element">
              <div className="pd-icon">
                <img src="/assets/img/department.png" alt="" />
              </div>
              <div className="pd-data-value">
                <h6>Business Category</h6>
                <p>{vendorOrganisations?.department || "Not provided yet"}</p>
              </div>
            </div>
          </>
        ) : (
          <div className="col">
            <div className="row row_gap">
              <div className="col-lg-4 col-md-6 col-sm-12">
                <div className="form-field">
                  <label className="input-label required-field">
                    Organisation Name
                  </label>
                  <input
                    type="text"
                    value={vendorDetailFormData.organisationName}
                    onChange={(e) =>
                      setVendorDetailFormData((prev) => ({
                        ...prev,
                        organisationName: e.target.value.trim().toUpperCase(),
                      }))
                    }
                    placeholder="Enter Organisation Name"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <div className="form-field">
                  <label className="input-label required-field">
                    Date of Joining
                  </label>
                  <input
                    type="date"
                    value={vendorDetailFormData.dateOfJoining}
                    onChange={(e) =>
                      setVendorDetailFormData((prev) => ({
                        ...prev,
                        dateOfJoining: e.target.value,
                      }))
                    }
                    placeholder="Select Date of Joining"
                    className="form-input"
                  />
                </div>
              </div>

              <div className="col-lg-4 col-md-6 col-sm-12">
                <div className="form-field">
                  <label className="input-label required-field">
                    Business Category
                  </label>
                  <Select
                    value={vendorDetailFormData.department}
                    onChange={(selected) =>
                      setVendorDetailFormData((prev) => ({
                        ...prev,
                        department: selected,
                      }))
                    }
                    options={departmentOptions}
                    placeholder="Select Department"
                    className="react-select-container"
                    classNamePrefix="react-select"
                  />
                </div>
              </div>
            </div>

            <div className="pd-edit-option">
              {employeeDetailUpdateMessage && (
                <p className={`edit-msg_area ${edMessageType}`}>
                  {employeeDetailUpdateMessage}
                </p>
              )}

              <button
                onClick={handleCancelClick}
                disabled={isEdUpdating}
                className={`pd-option-btn option-btn-cancel ${
                  isEdUpdating ? "disabled" : ""
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleUpdateEmployeeDetails}
                disabled={isEdUpdating}
                className={`pd-option-btn option-btn-save ${
                  isEdUpdating ? "disabled" : ""
                }`}
              >
                {isEdUpdating ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorDetailsEmpDetails;
